import React, { useState, useEffect } from "react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";

/**
 * Compliance health view for admins/managers. Pulls /api/compliance/summary
 * and renders six metric cards plus two registration status badges.
 *
 * Period selector (today / 7d / 30d) triggers a re-fetch. 30-day window is
 * the default because it covers the typical TCR audit ask and smooths
 * weekly spikes.
 */

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const REGISTRATION_STATUS_COPY = {
  not_started: { label: "Not started", badge: "bg-gray-100 text-gray-700" },
  pending: { label: "Pending review", badge: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", badge: "bg-green-100 text-green-800" },
  rejected: { label: "Rejected", badge: "bg-red-100 text-red-800" },
};

function formatPercent(n) {
  // Defensive against null/undefined from a partial response.
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(n < 10 ? 2 : 1)}%`;
}

export default function ComplianceDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/compliance/summary?period=${period}`)
      .then((res) => setSummary(res.data))
      .catch((err) => {
        setError(err.response?.data?.message || "Failed to load compliance summary");
      })
      .finally(() => setLoading(false));
  }, [period]);

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Header: title + period selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance</h1>
          <p className="text-sm text-gray-500 mt-1">
            Opt-out rates, enforcement counters, and registration status for your org.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-xs font-medium text-gray-500">Period</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12 text-gray-400">Loading compliance data…</div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {!loading && !error && summary && (
        <>
          {/* Row 1: key rates */}
          <section>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Key rates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                label="Opt-out rate"
                value={formatPercent(summary.opt_out_rate?.percent)}
                sub={`${summary.opt_out_rate?.opt_outs || 0} opt-outs across ${summary.opt_out_rate?.messaged || 0} contacts messaged`}
                tone="amber"
              />
              <StatCard
                label="Message failure rate"
                value={formatPercent(summary.message_failure_rate?.percent)}
                sub={`${summary.message_failure_rate?.failures || 0} of ${summary.message_failure_rate?.sent || 0} terminal sends`}
                tone="red"
              />
              <StatCard
                label="DNC list size"
                value={(summary.dnc_size || 0).toLocaleString()}
                sub="All-time cumulative (not period-scoped)"
                tone="gray"
              />
            </div>
          </section>

          {/* Row 2: enforcement counters */}
          <section>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Enforcement
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                label="Quiet-hours blocks"
                value={(summary.quiet_hours_blocks || 0).toLocaleString()}
                sub={
                  summary.quiet_hours_blocks > 0
                    ? "Non-zero means UI queue flow was bypassed — investigate"
                    : "Frontend queue flow working as expected"
                }
                tone={summary.quiet_hours_blocks > 0 ? "amber" : "green"}
              />
              <StatCard
                label="Scheduled pending"
                value={(summary.scheduled_pending || 0).toLocaleString()}
                sub="Messages queued for automatic dispatch in the next send window"
                tone="blue"
              />
              <StatCard
                label="Scheduled canceled"
                value={(summary.scheduled_canceled?.total || 0).toLocaleString()}
                sub={renderCanceledBreakdown(summary.scheduled_canceled?.by_reason)}
                tone="gray"
              />
            </div>
          </section>

          {/* Row 3: registration status placeholders */}
          <section>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Registrations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RegistrationBadge
                label="10DLC Brand Registration"
                subtitle="Required for standard 10-digit long-code messaging"
                status={summary.registrations?.brand_10dlc?.status || "not_started"}
                startedAt={summary.registrations?.brand_10dlc?.started_at}
              />
              <RegistrationBadge
                label="Toll-Free Verification"
                subtitle="Required for sending via toll-free numbers"
                status={summary.registrations?.toll_free?.status || "not_started"}
                startedAt={summary.registrations?.toll_free?.started_at}
              />
            </div>
          </section>

          {/* Footer: window range */}
          <p className="text-xs text-gray-400">
            Showing data from {formatDate(summary.period_start)} to{" "}
            {formatDate(summary.period_end)} (UTC).
          </p>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  Subcomponents — small, inline, no extraction overhead for v1
// ──────────────────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, tone = "gray" }) {
  // Tone just drives the left border accent color. Keeps cards visually
  // distinct without a full per-card color scheme.
  const toneBorders = {
    blue: "border-l-4 border-blue-500",
    green: "border-l-4 border-green-500",
    amber: "border-l-4 border-amber-500",
    red: "border-l-4 border-red-500",
    gray: "border-l-4 border-gray-300",
  };
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 ${
        toneBorders[tone] || toneBorders.gray
      }`}
    >
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-2">{sub}</p>}
    </div>
  );
}

function RegistrationBadge({ label, subtitle, status, startedAt }) {
  const copy = REGISTRATION_STATUS_COPY[status] || REGISTRATION_STATUS_COPY.not_started;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        {startedAt && (
          <p className="text-xs text-gray-400 mt-1">
            Submitted {formatDate(startedAt)}
          </p>
        )}
      </div>
      <span className={`text-xs font-medium px-3 py-1 rounded-full ${copy.badge}`}>
        {copy.label}
      </span>
    </div>
  );
}

// Render the canceled-reason breakdown as a compact inline list under the
// main count. Hidden when we don't have any cancellations to describe.
function renderCanceledBreakdown(byReason) {
  if (!byReason || Object.keys(byReason).length === 0) {
    return "No cancellations in this period";
  }
  const parts = Object.entries(byReason)
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => `${reason.replace(/_/g, " ")}: ${count}`);
  return parts.join(" · ");
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
